import Strategy from './Strategy.js'
import { con, sfu } from '../messages'
import { jsonRpc } from '../JsonRpc'
import { localStream } from 'stream/src/index.js'
import { Device, types } from 'mediasoup-client'
import { mediator, messenger } from '../index.js'
import { getMediasoupHandlerName } from '../../../browser/src/index.js'

// A consume that failed for a reason that may pass (media server not reachable or overloaded, request timeout, no
// result from a signaling server up to 5.1.1) is asked for once more after this delay
const CONSUME_RETRY_DELAY = 1000
// Error codes of a failed consume (signaling SFUHandler) after which asking again cannot help: the party is not (or no
// longer) in the call and a re-join consumes again, the producer is gone (sfu:closeProducer follows), the media server
// does not know the transport, or the device cannot receive the producer's codec
const FINAL_CONSUME_ERRORS = ['CALL_REQUIRED', 'PRODUCER_NOT_FOUND', 'TRANSPORT_NOT_FOUND', 'CANNOT_CONSUME']
// Expected after leaving the call or when the producer closed meanwhile, so not reported
const QUIET_CONSUME_ERRORS = ['CALL_REQUIRED', 'PRODUCER_NOT_FOUND']

interface ProducerAppData extends types.AppData {
  source: string
  trackId: string
}

type Producer = types.Producer<ProducerAppData>
type ProducerMap = Map<string, Producer>
type ConsumerMap = Map<string, types.Consumer>
type DataConsumerMap = Map<string, types.DataConsumer>
type TransportResponse = {
  id: string,
  iceParameters: types.IceParameters
  iceCandidates: types.IceCandidate[]
  dtlsParameters: types.DtlsParameters
  sctpParameters: types.SctpParameters
}
type ConsumeResponse = {
  id: string
  kind: types.MediaKind
  rtpParameters: types.RtpParameters
}
type ConsumeDataResponse = {
  id: string
  sctpStreamParameters: types.SctpStreamParameters
  label: string
  protocol: string
}
class SFUStrategy extends Strategy {
  name: string
  peerId: string | null
  isReady: boolean
  private device!: types.Device

  private sendTransport!: Promise<types.Transport>
  private sendTransportResolve!: (transport: types.Transport) => void
  private recvTransport!: Promise<types.Transport>
  private recvTransportResolve!: (transport: types.Transport) => void

  private producers: ProducerMap
  // The last camera/microphone produce step of the current initialize() (see syncProducer)
  private producing: Promise<unknown> = Promise.resolve()
  private consumers: ConsumerMap
  private dataConsumers: DataConsumerMap
  private dataProducer?: types.DataProducer
  private transports: types.Transport[] = [] // send and recv transport of the current initialize()
  // (Data) producer ids whose consume is in progress on that recv transport, also while waiting to ask again: a second
  // sfu:consume for the same producer meanwhile would create a second consumer
  private pendingConsumes = new Map<string, types.Transport>()
  // (Data) producers closed while their consume was in progress: the consumer is dropped when it arrives
  private closedWhilePending = new Set<string>()

  constructor() {
    super()
    this.name = 'sfu'
    this.producers = new Map()
    this.consumers = new Map()
    this.dataConsumers = new Map()
    this.peerId = null
    this.isReady = false
    jsonRpc.on('sfu:consume', this.handleConsume)
    jsonRpc.on('sfu:consumeData', this.handleConsumeData)
    jsonRpc.on('sfu:recordingStarted', this.handleRecordingStarted)
    jsonRpc.on('sfu:recordingStopped', this.handleRecordingStopped)
    jsonRpc.on('sfu:closeProducer', this.handleCloseProducer)
    jsonRpc.on('sfu:closeDataProducer', this.handleCloseDataProducer)
  }

  async initialize() {
    this.producers.forEach(producer => {
      producer.close()
    })
    this.producers.clear()
    this.consumers.forEach(consumer => {
      consumer.close()
    })
    this.consumers.clear()
    this.dataConsumers.forEach(dataConsumer => {
      dataConsumer.close()
    })
    this.dataConsumers.clear()
    this.pendingConsumes.clear()
    this.closedWhilePending.clear()
    this.peerId = null
    this.isReady = false
    this.transports = []
    this.producing = Promise.resolve()
    this.sendTransport = new Promise(resolve => {
      this.sendTransportResolve = resolve
    })
    this.recvTransport = new Promise(resolve => {
      this.recvTransportResolve = resolve
    })
    const { rtpCapabilities } = await jsonRpc.sendAndWait<{ rtpCapabilities: types.RtpCapabilities }>(sfu.getRouterRtpCapabilitiesMessage())
    if (rtpCapabilities.headerExtensions) {
      rtpCapabilities.headerExtensions = rtpCapabilities.headerExtensions.filter(
        ext => ext.uri !== 'urn:3gpp:video-orientation'
      )
    }
    if (!this.device) {
      this.device = await Device.factory({ handlerName: await getMediasoupHandlerName() })
    }
    if (!this.device.loaded) {
      try {
        await this.device.load({ routerRtpCapabilities: rtpCapabilities })
      } catch (error) {
        messenger.emit('log:dumpTrigger', { reason: 'device-load-error', error })
        console.info('Error loading device with router RTP capabilities', { error })
        throw error
      }
    }
    await this.createTransports()
    messenger.subscribe(this.name, 'strategy:muted', this.handleMuted)
    messenger.subscribe(this.name, 'strategy:unmuted', this.handleUnmuted)
  }

  handleConsume = async (event: { detail: any }) => {
    const data = event.detail
    console.info(`sfu:consume <= ${JSON.stringify(data)}`)
    const transport = await this.recvTransport
    const { producerId, source } = data
    const what = `sfu:consume ${producerId}`
    if (!transport) {
      // Announced before this party initialized SFU (its con:strategy came later): nothing consumes it
      this.reportConsumeError(transport, what, new Error('SFU_NOT_INITIALIZED'))
      return
    }
    // Verify if consumer for this producerId already exists or is being created
    if (this.consumers.has(producerId) || this.pendingConsumes.get(producerId) === transport) {
      console.info('Consumer already exists for producerId:', producerId)
      return
    }
    this.pendingConsumes.set(producerId, transport)
    try {
      const response = await this.requestConsume<ConsumeResponse>(
        transport,
        sfu.consumeMessage(producerId, transport.id, this.device!.rtpCapabilities),
        what
      )
      if (!response || this.closedWhilePending.has(producerId)) return
      const { id, kind, rtpParameters } = response
      const consumer = await transport.consume({
        id,
        producerId,
        kind,
        rtpParameters,
      })
      if (this.closedWhilePending.has(producerId)) {
        consumer.close()
        return
      }
      this.consumers.set(consumer.producerId, consumer)
      consumer.observer.on('close', () => {
        this.consumers.delete(consumer.producerId)
      })
      jsonRpc.send(sfu.resumeConsumerMessage(transport.id, consumer.id))
      const stream = new MediaStream([consumer.track])
      if (source === 'screen') {
        this.emit('strategy:screensharingStarted:viewer', { stream })
      } else {
        this.emit(`strategy:stream:${kind as 'video' | 'audio'}`, { stream })
      }
    } catch (error) {
      this.reportConsumeError(transport, what, error) // the local consume failed
    } finally {
      this.endPendingConsume(producerId, transport)
    }
  }

  handleConsumeData = async (event: { detail: any }) => {
    const data = event.detail
    console.info(`sfu:consumeData <= ${JSON.stringify(data)}`)
    const transport = await this.recvTransport
    const { dataProducerId } = data
    if (!dataProducerId) {
      return
    }
    const what = `sfu:consumeData ${dataProducerId}`
    if (!transport) {
      this.reportConsumeError(transport, what, new Error('SFU_NOT_INITIALIZED'))
      return
    }
    // verify if data consumer for this dataProducerId already exists or is being created
    if (this.dataConsumers.has(dataProducerId) || this.pendingConsumes.get(dataProducerId) === transport) {
      console.info('Data consumer already exists for dataProducerId:', dataProducerId)
      return
    }
    this.pendingConsumes.set(dataProducerId, transport)
    try {
      const response = await this.requestConsume<ConsumeDataResponse>(
        transport,
        sfu.consumeDataMessage(dataProducerId, transport.id),
        what
      )
      if (!response || this.closedWhilePending.has(dataProducerId)) return
      const { id, sctpStreamParameters, label, protocol } = response

      const dataConsumer = await transport.consumeData({
        id,
        dataProducerId,
        sctpStreamParameters,
        label,
        protocol,
      })
      if (this.closedWhilePending.has(dataProducerId)) {
        dataConsumer.close()
        return
      }
      dataConsumer.observer.on('close', () => {
        this.dataConsumers.delete(dataConsumer.dataProducerId)
      })
      dataConsumer.on('message', (message: string) => {
        let parsed: any
        try {
          parsed = JSON.parse(message)
        } catch {
          parsed = message
        }
        this.emit('strategy:data', parsed)
      })
      this.dataConsumers.set(dataConsumer.dataProducerId, dataConsumer)
    } catch (error) {
      this.reportConsumeError(transport, what, error) // the local consume failed
    } finally {
      this.endPendingConsume(dataProducerId, transport)
    }
  }

  // Asks the server for a (data) consumer, once more after a failure that may pass. Ends at once on a failure that
  // cannot pass (FINAL_CONSUME_ERRORS) and when the transport is closed (strategy destroyed or re-initialized: the
  // new transports consume the producers again)
  private async requestConsume<R extends { id: string }>(
    transport: types.Transport,
    message: Record<string, any>,
    what: string
  ): Promise<R | undefined> {
    let error: any
    for (let attempt = 1; attempt <= 2; attempt++) {
      if (attempt > 1) await new Promise(resolve => setTimeout(resolve, CONSUME_RETRY_DELAY))
      if (transport.closed) return undefined
      try {
        const response = await jsonRpc.sendAndWait<R | undefined>(message)
        if (response?.id) return response
        error = new Error('CONSUME_FAILED') // no result: a signaling server up to 5.1.1 that failed it or had no call
      } catch (e: any) {
        error = e // a JSON-RPC error { code, message }, a request timeout or the closed websocket
        if (FINAL_CONSUME_ERRORS.includes(e?.message)) break
      }
    }
    this.reportConsumeError(transport, what, error)
    return undefined
  }

  // Reported (log dump, strategy:error) instead of an error thrown out of the notification handler, which left the
  // producer unconsumed without a trace: a black or silent tile in an SFU call
  private reportConsumeError(transport: types.Transport | undefined, what: string, error: any) {
    const code = error?.message ?? String(error)
    if (transport?.closed || QUIET_CONSUME_ERRORS.includes(code)) return
    const text = `${what}: ${code}`
    messenger.emit('log:dumpTrigger', { reason: 'consume-error', error: text })
    this.handleError(text)
  }

  private endPendingConsume(producerId: string, transport: types.Transport) {
    if (this.pendingConsumes.get(producerId) === transport) {
      this.pendingConsumes.delete(producerId)
      this.closedWhilePending.delete(producerId)
    }
  }

  broadcast(data: any): void {
    if (this.dataProducer && this.dataProducer.readyState === 'open') {
      const message = typeof data === 'string' ? data : JSON.stringify(data)
      this.dataProducer.send(message)
    }
  }

  async replaceTrack(type: string, _oldTrack: MediaStreamTrack, newTrack: MediaStreamTrack, stream: MediaStream): Promise<void> {
    const transport = await this.sendTransport
    let replaced = false
    for (const producer of this.producers.values()) {
      if (producer.appData.source !== 'screen' && producer.kind === type) {
        await producer.replaceTrack({ track: newTrack })
        console.info(`${type} track replaced`)
        jsonRpc.send(sfu.syncRecordingMessage(transport.id, producer.id))
        replaced = true
      }
    }
    // The kind has no producer: its track was dead when the SFU started (e.g. lost during the switch) and is recovered now
    if (!replaced) await this.addTrack(type, newTrack, stream)
  }

  // A camera or microphone without a producer gets one when its track comes: blocked when joining and allowed later, or
  // lost during the switch to SFU and recovered by PermissionsProcedure. The signaling announces the new producer to the
  // other parties (sfu:consume), and the media server adds it to a running recording
  async addTrack(type: string, _track?: MediaStreamTrack, _stream?: MediaStream): Promise<void> {
    try {
      await this.syncProducer(type as types.MediaKind)
    } catch (error: any) {
      this.handleError(`Producing the ${type} track failed: ${error?.message ?? error}`)
    }
  }

  async destroy() {
    super.destroy()
    try {
      for (const producer of this.producers.values()) {
        producer.close()
      }
      this.producers.clear()
      for (const consumer of this.consumers.values()) {
        consumer.close()
      }
      this.consumers.clear()
      const sendTransport = await this.sendTransport
      if (sendTransport) {
        sendTransport.close()
      }
      const recvTransport = await this.recvTransport
      if (recvTransport) {
        recvTransport.close()
      }
      this.dataProducer?.close()
      for (const dataConsumer of this.dataConsumers.values()) {
        dataConsumer.close()
      }
      this.dataConsumers.clear()
      if (this.peerId) {
        await new Promise(resolve => {
          jsonRpc.send(
            sfu.leaveRoomMessage({
              peerId: this.peerId,
            }),
            () => {
              resolve(null)
            }
          )
        })
      }
      console.info('SFU strategy destroyed')
    } catch (error) {
      console.info('Error destroying SFU strategy', { error })
    }
    messenger.unsubscribe('strategy:muted')
    messenger.unsubscribe('strategy:unmuted')
  }

  isStrategyReady(): boolean {
    // A transport whose connection is lost stays so: no server method restarts its ICE (transport.restartIce needs new
    // iceParameters from the server), only a websocket reconnect or a reload initializes the strategy again
    return (
      this.isReady &&
      this.transports.every(({ connectionState }) => connectionState !== 'failed' && connectionState !== 'disconnected')
    )
  }

  handleError = (error: any): void => {
    console.info(`SFU strategy error: ${error}`, { error })
    console.error('SFU Strategy Error:', error)
    this.emit('strategy:error', { error })
  }

  async getTotalBits(): Promise<number> {
    let totalBits = 0
    const peers = [...this.producers.values(), ...this.consumers.values()]
    for (const peer of peers) {
      const stats = await peer.getStats()
      for (const report of stats.values()) {
        if (report.type === 'transport') {
          totalBits += report.bytesSent || 0
          totalBits += report.bytesReceived || 0
        }
      }
    }
    return totalBits
  }

  async startRecording() {
    await this.sendTransport
    await this.recvTransport
    jsonRpc.send(sfu.startRecordingMessage())
  }

  stopRecording() {
    jsonRpc.send(sfu.stopRecordingMessage())
  }

  handleRecordingStarted = () => {
    console.info('sfu:recordingStarted')
    this.emit('strategy:recording:started', void 0)
  }

  handleRecordingStopped = () => {
    console.info('sfu:recordingStopped')
    this.emit('strategy:recording:stopped', void 0)
  }

  shareScreen = async (stream: MediaStream) => {
    await this.produceTrack(stream.getVideoTracks()[0], 'video', 'screen')
  }

  destroyScreensharing = () => {
    for (const producer of this.producers.values()) {
      if (producer.appData.source === 'screen') {
        producer.close()
        this.producers.delete(producer.id)
        this.closeProducer(producer.id)
        break
      }
    }
  }

  private async createTransports() {
    const { id, iceParameters, iceCandidates, dtlsParameters, sctpParameters } = await jsonRpc.sendAndWait<TransportResponse>(sfu.createTransportMessage())
    await this.createLocalSendTransport(id, iceParameters, iceCandidates, dtlsParameters, sctpParameters)
    const {
      id: recvId,
      iceParameters: recvIceParameters,
      iceCandidates: recvIceCandidates,
      dtlsParameters: recvDtlsParameters,
      sctpParameters: recvSctpParameters,
    } = await jsonRpc.sendAndWait<TransportResponse>(sfu.createTransportMessage())
    await this.createLocalRecvTransport(
      recvId,
      recvIceParameters,
      recvIceCandidates,
      recvDtlsParameters,
      recvSctpParameters
    )
    await this.start()
  }

  private async createLocalSendTransport(
    id: string,
    iceParameters: types.IceParameters,
    iceCandidates: types.IceCandidate[],
    dtlsParameters: types.DtlsParameters,
    sctpParameters?: types.SctpParameters
  ) {
    const transport = this.device.createSendTransport({
      id,
      iceParameters,
      iceCandidates,
      dtlsParameters,
      sctpParameters,
      iceServers: mediator.session?.configuration.iceServers || [],
    })
    transport.on('connect', async ({ dtlsParameters }, callback, errorBack) => {
      try {
        await jsonRpc.sendAndWait(sfu.connectTransportMessage(transport!.id, dtlsParameters))
        callback()
      } catch (error: any) {
        messenger.emit('log:dumpTrigger', { reason: 'send-transport-connect-error', error: error })
        errorBack(error)
      }
    })
    transport.on('produce', async (data, callback, errorBack) => {
      const { kind, rtpParameters, appData } = data
      const { source, trackId } = appData as ProducerAppData
      try {
        const { id } = await jsonRpc.sendAndWait<{ id: string }>(
          sfu.produceMessage(transport!.id, kind, rtpParameters, source, trackId)
        )
        callback({ id })
      } catch (error: any) {
        messenger.emit('log:dumpTrigger', { reason: 'produce-error', error: error })
        errorBack(error)
      }
    })
    transport.on('producedata', async (data, callback, errorBack) => {
      const { sctpStreamParameters, label, protocol, appData } = data
      try {
        const { id } = await jsonRpc.sendAndWait<{ id: string }>(
          sfu.produceDataMessage(transport!.id, sctpStreamParameters, appData, protocol, label)
        )
        callback({ id })
      } catch (error: any) {
        messenger.emit('log:dumpTrigger', { reason: 'produce-data-error', error: error })
        errorBack(error)
      }
    })
    transport.on('connectionstatechange', async connectionState => {
      if (connectionState === 'failed' || connectionState === 'disconnected') {
        console.info(`WebRTC connection state is: ${connectionState}`)
      }
      // 'disconnected' often heals by itself; a dump for each blip would only add noise
      if (connectionState === 'failed') {
        messenger.emit('log:dumpTrigger', { reason: 'send-transport-connection-failed', error: { connectionState } })
      }
    })
    this.transports.push(transport)
    this.sendTransportResolve(transport)
    console.info(`Send transport ${transport.id} created`)
  }

  private async createLocalRecvTransport(
    id: string,
    iceParameters: types.IceParameters,
    iceCandidates: types.IceCandidate[],
    dtlsParameters: types.DtlsParameters,
    sctpParameters?: types.SctpParameters
  ) {
    const transport = this.device.createRecvTransport({
      id,
      iceParameters,
      iceCandidates,
      dtlsParameters,
      sctpParameters,
      iceServers: mediator.session?.configuration.iceServers || [],
    })
    transport.on('connect', async ({ dtlsParameters }, callback, errorBack) => {
      try {
        await jsonRpc.sendAndWait(sfu.connectTransportMessage(transport!.id, dtlsParameters))
        callback()
      } catch (error: any) {
        messenger.emit('log:dumpTrigger', { reason: 'recv-transport-connect-error', error: error })
        errorBack(error)
      }
    })
    transport.on('connectionstatechange', async connectionState => {
      if (connectionState === 'failed' || connectionState === 'disconnected') {
        console.info(`WebRTC connection state is: ${connectionState}`)
      }
      // 'disconnected' often heals by itself; a dump for each blip would only add noise
      if (connectionState === 'failed') {
        messenger.emit('log:dumpTrigger', { reason: 'recv-transport-connection-failed', error: { connectionState } })
      }
    })
    this.transports.push(transport)
    this.recvTransportResolve(transport)
    console.info(`Recv transport ${transport.id} created`)
  }

  private async start() {
    await this.startProducing()
    await this.createDataProducer()
    jsonRpc.send(con.readyMessage(), (error: any, response: any) => {
      if (!error) {
        this.isReady = true
        this.emit('strategy:ready', void 0)
      } else {
        console.info(`endpoint: con:ready couldn\'t been sent`, {
          response,
        })
      }
    })
  }

  // Only live tracks: mediasoup-client rejects an ended one ('track ended'), and that rejection ended this start before
  // the microphone, the data producer and con:ready (no audio, chat or profile sync for the rest of the call). A kind
  // whose track ended is being re-acquired by PermissionsProcedure meanwhile (1 s debounce), and addTrack/replaceTrack
  // produce it when its new track comes
  private async startProducing() {
    await this.syncProducer('video')
    await this.syncProducer('audio')
  }

  // Gives the kind's live camera/microphone track a producer, or moves the kind's producer to it. One step at a time:
  // startProducing and a track that comes meanwhile (addTrack, replaceTrack without a producer) never produce a kind
  // twice. A muted (disabled) track gives a paused producer (mediasoup-client), as handleMuted would have left it
  private syncProducer(kind: types.MediaKind): Promise<void> {
    const sendTransport = this.sendTransport
    const step = this.producing.then(async () => {
      const transport = await sendTransport
      // Not initialized, destroyed, or initialized again since (that start produces the kind)
      if (!transport || transport.closed || sendTransport !== this.sendTransport) return
      // None or ended: being re-acquired by PermissionsProcedure, whose new track comes here again
      const track = localStream[kind].stream.value?.getTracks().find((t: MediaStreamTrack) => t.readyState === 'live')
      if (!track) return
      const producer = [...this.producers.values()].find(p => p.appData.source !== 'screen' && p.kind === kind)
      if (!producer) {
        await this.produceTrack(track, kind, kind === 'video' ? 'camera' : 'microphone')
      } else if (producer.track !== track) {
        // Replaced while the kind was being produced (replaceTrack found no producer yet), or stopped and captured anew
        await producer.replaceTrack({ track })
        jsonRpc.send(sfu.syncRecordingMessage(transport.id, producer.id))
      }
    })
    this.producing = step.catch(() => {})
    return step
  }

  private async produceTrack(track: MediaStreamTrack, kind: string, source: string): Promise<types.Producer> {
    const transport = await this.sendTransport
    try {
      const producer = await transport.produce<ProducerAppData>({
        track,
        stopTracks: false,
        appData: {
          source,
          trackId: track.id,
        },
      })
      this.producers.set(producer.id, producer)
      producer.observer.on('close', () => {
        this.producers.delete(producer.id) // keyed by id: a closed producer must not count as the kind's producer
        this.closeProducer(producer.id)
      })
      return producer
    } catch (error) {
      console.info(`Failed to produce ${kind}`, { error })
      throw error
    }
  }

  private async closeProducer(producerId: string): Promise<void> {
    const transport = await this.sendTransport
    await jsonRpc.sendAndWait(sfu.closeProducerMessage(transport.id, producerId))
  }

  private async createDataProducer(label = 'data', protocol = '') {
    const transport = await this.sendTransport
    try {
      this.dataProducer = await transport.produceData({
        ordered: true,
        label,
        protocol,
      })
    } catch (error) {
      console.info(`Failed to produce data ${error}`)
      throw error
    }
  }
  getMeasureStats = () => {
    const peers = [...this.producers.values(), ...this.consumers.values()]
    for (const peer of peers) {
      if (peer.kind === 'video') {
        return peer.getStats()
      }
    }
    return null
  }
  handleCloseProducer = ({ detail: { producerId } }: { detail: { producerId: string } }) => {
    console.info(`sfu:closeProducer <= ${JSON.stringify({ producerId })}`)
    if (this.pendingConsumes.has(producerId)) this.closedWhilePending.add(producerId)
    this.consumers.get(producerId)?.close()
    this.consumers.delete(producerId)
  }
  handleCloseDataProducer = ({ detail: { dataProducerId } }: { detail: { dataProducerId: string } }) => {
    console.info(`sfu:closeDataProducer <= ${JSON.stringify({ dataProducerId })}`)
    if (this.pendingConsumes.has(dataProducerId)) this.closedWhilePending.add(dataProducerId)
    this.dataConsumers.forEach(dataConsumer => {
      if (dataConsumer.dataProducerId === dataProducerId) {
        dataConsumer.close()
        this.dataConsumers.delete(dataConsumer.id)
      }
    })
  }
  handleMuted = ({ type }: { type: string }) => {
    this.producers.forEach(producer => {
      if (producer.appData.source !== 'screen' && producer.kind === type) {
        producer.pause()
      }
    })
  }
  handleUnmuted = ({ type }: { type: string }) => {
    this.producers.forEach(producer => {
      if (producer.appData.source !== 'screen' && producer.kind === type) {
        producer.resume()
      }
    })
  }
}

const sfuStrategy = new SFUStrategy()
export { sfuStrategy }
